import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { io } from "socket.io-client";

import { INBOX_CHANGED_EVENT, type InboxChangedEvent } from "@mandoob/shared";

import { getGetCountsQueryKey } from "@/api/inbox";
import { appConfig, getAccessToken } from "@/config";
import { hapticImpact } from "@/lib/haptics";
import { showMessageNotification } from "@/lib/notifications";
import { playMessageSound } from "@/lib/sound";

// Modeled on the reference use-inbox-realtime.ts, with SignalR replaced by
// socket.io. The server authenticates the handshake via `auth.token` and joins
// the socket to its driver room, so there is nothing to subscribe to
// explicitly; reconnects are handled by socket.io itself.
export function useInboxRealtime() {
  const queryClient = useQueryClient();

  useEffect(() => {
    const options = {
      path: "/socket.io",
      auth: { token: getAccessToken() ?? "" },
    };
    const socket = appConfig.apiBaseUrl
      ? io(appConfig.apiBaseUrl, options)
      : io(options);

    const invalidate = (conversationId?: string | null) => {
      // Prefix keys: matches every param variant, including the infinite list.
      void queryClient.invalidateQueries({
        queryKey: ["/api/inbox/conversations"],
      });
      void queryClient.invalidateQueries({ queryKey: getGetCountsQueryKey() });
      if (conversationId) {
        void queryClient.invalidateQueries({
          queryKey: [`/api/inbox/conversations/${conversationId}/thread`],
        });
      }
    };

    socket.on(INBOX_CHANGED_EVENT, (event: InboxChangedEvent) => {
      invalidate(event?.conversationId);

      const inbound =
        event?.reason === "message" &&
        event.preview?.direction === "Inbound" &&
        !!event.conversationId;

      // On screen the list, badge and open thread already update live, so a
      // banner would be noise — a chime and a buzz are the right weight.
      if (inbound && document.visibilityState === "visible") {
        playMessageSound();
        hapticImpact("light");
        return;
      }

      if (inbound && event.conversationId && event.preview) {
        void showMessageNotification({
          conversationId: event.conversationId,
          title: event.preview.clientName,
          body: event.preview.text,
        });
      }
    });

    // A reconnect may have missed events — refetch everything once.
    socket.io.on("reconnect", () => invalidate());

    return () => {
      socket.off(INBOX_CHANGED_EVENT);
      socket.disconnect();
    };
  }, [queryClient]);
}
